import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import PostMioLogo from '../components/PostMioLogo';

// ── i18n ───────────────────────────────────────────────────
const UI_TEXT = {
  IT: {
    heading:     'Attiva Account',
    subheading:  'PostMio™ Premium',
    successMsg:  'Perfetto! Controlla la tua email. Ti abbiamo inviato un link per creare la tua password su Postmio.com.',
    fieldLabel:  'Email del Checkout',
    placeholder: 'nome@esempio.com',
    btnLoading:  'Generazione link...',
    btnSubmit:   'Crea la mia Password',
    havePassword:'Hai già una password? Accedi →',
  },
  EN: {
    heading:     'Activate Account',
    subheading:  'PostMio™ Premium',
    successMsg:  'Done! Check your email. We\'ve sent you a link to create your password on Postmio.com.',
    fieldLabel:  'Checkout Email',
    placeholder: 'name@example.com',
    btnLoading:  'Generating link...',
    btnSubmit:   'Create my Password',
    havePassword:'Already have a password? Log in →',
  },
  ES: {
    heading:     'Activar cuenta',
    subheading:  'PostMio™ Premium',
    successMsg:  '¡Listo! Revisa tu correo electrónico. Te hemos enviado un enlace para crear tu contraseña en Postmio.com.',
    fieldLabel:  'Email de pago',
    placeholder: 'nombre@ejemplo.com',
    btnLoading:  'Generando enlace...',
    btnSubmit:   'Crear mi contraseña',
    havePassword:'¿Ya tienes contraseña? Iniciar sesión →',
  },
  DE: {
    heading:     'Konto aktivieren',
    subheading:  'PostMio™ Premium',
    successMsg:  'Fertig! Prüfen Sie Ihre E-Mail. Wir haben Ihnen einen Link geschickt, um Ihr Passwort auf Postmio.com zu erstellen.',
    fieldLabel:  'E-Mail der Bestellung',
    placeholder: 'name@beispiel.de',
    btnLoading:  'Link wird generiert...',
    btnSubmit:   'Mein Passwort erstellen',
    havePassword:'Haben Sie bereits ein Passwort? Anmelden →',
  },
  FR: {
    heading:     'Activer le compte',
    subheading:  'PostMio™ Premium',
    successMsg:  'Terminé ! Vérifiez votre e-mail. Nous vous avons envoyé un lien pour créer votre mot de passe sur Postmio.com.',
    fieldLabel:  'E-mail de paiement',
    placeholder: 'nom@exemple.fr',
    btnLoading:  'Génération du lien...',
    btnSubmit:   'Créer mon mot de passe',
    havePassword:'Vous avez déjà un mot de passe ? Se connecter →',
  },
  PL: {
    heading:     'Aktywuj konto',
    subheading:  'PostMio™ Premium',
    successMsg:  'Gotowe! Sprawdź swoją skrzynkę. Wysłaliśmy Ci link do utworzenia hasła na Postmio.com.',
    fieldLabel:  'Email z zamówienia',
    placeholder: 'imie@przyklad.pl',
    btnLoading:  'Generowanie linku...',
    btnSubmit:   'Utwórz moje hasło',
    havePassword:'Masz już hasło? Zaloguj się →',
  },
}

function detectLang() {
  const lang = (navigator.language || '').toLowerCase()
  if (lang.startsWith('pl')) return 'PL'
  if (lang.startsWith('en')) return 'EN'
  if (lang.startsWith('es')) return 'ES'
  if (lang.startsWith('de')) return 'DE'
  if (lang.startsWith('fr')) return 'FR'
  return 'IT'
}

function renderTM(s) {
  if (typeof s !== 'string' || !s.includes('™')) return s
  const i = s.indexOf('™')
  return <>{s.slice(0, i)}<sup style={{fontSize:'0.5em',verticalAlign:'super',lineHeight:0}}>™</sup>{s.slice(i + 1)}</>
}

export default function SetupAccount() {
  const t = UI_TEXT[detectLang()]
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  async function handlePasswordReset(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/confirm-password`,
      });

      if (resetError) throw resetError;

      setMessage(t.successMsg);
    } catch (err) {
      console.error('Setup Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-4"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block">
            <PostMioLogo size="md" variant="dark" />
          </Link>
          <p className="text-[#7A7A7A] text-xs uppercase tracking-[0.3em] mt-2">
            {renderTM(t.subheading)}
          </p>
        </div>

        {/* Card */}
        <div className="bg-[#141414] border border-[#2C2C2C] rounded-2xl p-8 shadow-xl">

          <h2 className="text-2xl font-bold text-[#F0EDE8] text-center mb-6" style={{ fontFamily: 'Lora, serif' }}>
            {t.heading}
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-[#A02020]/10 border border-[#A02020]/30 rounded-xl text-sm text-[#F0EDE8] text-center">
              {error}
            </div>
          )}

          {message ? (
            <div className="p-5 bg-[#C9922A]/10 border border-[#C9922A]/30 rounded-xl text-sm text-[#F0EDE8] text-center leading-relaxed">
              {message}
            </div>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#7A7A7A] uppercase tracking-[0.15em] mb-2">
                  {t.fieldLabel}
                </label>
                <input
                  type="email"
                  required
                  placeholder={t.placeholder}
                  className="w-full bg-[#0A0A0A] border border-[#2C2C2C] rounded-xl px-5 py-4 text-[#F0EDE8] text-sm placeholder-[#606060] focus:border-[#C9922A] outline-none transition-all"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#C9922A] hover:bg-[#E5B050] disabled:opacity-40 disabled:cursor-not-allowed text-[#0A0A0A] font-bold py-4 rounded-xl text-sm uppercase tracking-[0.2em] transition-all mt-2"
              >
                {loading ? t.btnLoading : t.btnSubmit}
              </button>
            </form>
          )}

          <p className="mt-8 text-center text-[#606060] text-xs">
            <a href="/login" className="text-[#C9922A] hover:text-[#E5B050] transition-colors">
              {t.havePassword}
            </a>
          </p>
          <p className="mt-4 text-center text-xs">
            <a href="/privacy" className="text-[#606060] hover:text-[#A0A0A0] transition-colors">Privacy Policy</a>
          </p>

        </div>
      </div>
    </div>
  );
}
